import type { JSX } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useReturnVerificationForm } from '../hooks/useReturnVerificationForm';
import type { InspectionVerification } from '../types/inspection.types';

interface ReturnVerificationDialogProps {
  verification: InspectionVerification | null;
  onClose: () => void;
}

export function ReturnVerificationDialog({
  verification,
  onClose,
}: ReturnVerificationDialogProps): JSX.Element | null {
  if (!verification) {
    return null;
  }

  return (
    <ReturnVerificationDialogContent
      key={verification.id}
      verification={verification}
      onClose={onClose}
    />
  );
}

function ReturnVerificationDialogContent({
  verification,
  onClose,
}: {
  verification: InspectionVerification;
  onClose: () => void;
}): JSX.Element {
  const { comment, setComment, error, isLoading, submit } = useReturnVerificationForm(
    verification.id,
    onClose
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Return verification</DialogTitle>
          <DialogDescription>
            Tell the agent why the verification from {verification.refereeName} is returned.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="inspection-comment">Comment</Label>
          <Textarea
            id="inspection-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={isLoading} onClick={submit}>
            {isLoading ? 'Returning...' : 'Return to agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
